import apiClient from "./apiClient";

export interface UploadOptions {
  fileName?: string;
  folder?: string;
}

export interface UploadResult {
  uploadUrl: string;
  downloadUrl: string;
  key: string;
}

class MediaUploadService {
  /**
   * Compress image before upload to reduce file size and speed up upload
   */
  private async compressImage(file: File, maxWidth: number = 1200, quality: number = 0.85): Promise<File> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Resize if needed
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                console.log(`[MediaUpload] Compressed ${file.name}: ${(file.size / 1024).toFixed(0)}KB → ${(compressedFile.size / 1024).toFixed(0)}KB`);
                resolve(compressedFile);
              } else {
                reject(new Error('Compression failed'));
              }
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  }

  /**
   * Upload an image by proxying it through the backend to circumvent browser CORS issues.
   * This is the most production-grade solution for the web app.
   */
  async uploadImage(file: File, options?: UploadOptions): Promise<string | null> {
    try {
      console.log(`[MediaUpload] Starting upload: ${file.name} (${(file.size / 1024).toFixed(0)}KB)`);

      // Compress image if it's larger than 500KB
      let uploadFile = file;
      if (file.size > 500 * 1024 && file.type.startsWith('image/')) {
        console.log('[MediaUpload] Compressing image...');
        uploadFile = await this.compressImage(file);
      }

      const formData = new FormData();
      formData.append('file', uploadFile);
      if (options?.folder) {
        formData.append('folder', options.folder);
      }

      const startTime = Date.now();
      const response = await apiClient.post<UploadResult>(
        '/media/upload-direct',
        formData,
        {
          headers: {
            'Content-Type': undefined
          },
          timeout: 120000, // 2 minutes for uploads
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              console.log(`[MediaUpload] Upload progress: ${percentCompleted}%`);
            }
          },
        }
      );

      const duration = ((Date.now() - startTime) / 1000).toFixed(1);
      console.log(`[MediaUpload] Upload completed in ${duration}s: ${response.data.downloadUrl}`);

      return response.data.downloadUrl;
    } catch (error: any) {
      console.error('[MediaUpload] Upload failed:', error.message);
      if (error.code === 'ECONNABORTED') {
        console.error('[MediaUpload] Upload timeout - file may be too large or connection too slow');
      }
      return null;
    }
  }

  /**
   * Same signature as mobile app's mediaUpload.uploadImage
   */
  async uploadImageWithToast(file: File, folder: string = 'uploads'): Promise<string | null> {
    return this.uploadImage(file, { folder });
  }
}

export const mediaUpload = new MediaUploadService();
export default mediaUpload;
